'use client';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CibWindows from '../icons/CibWindows';
import ReqTooltip from './ReqTooltip';
import { ButtonProps } from './types';

const CyberButton = (props: ButtonProps) => {
  const { className, href, onClick, children } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showReqDrawer, setShowReqDrawer] = useState(false);

  // 状态
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const rippleRef = useRef<HTMLSpanElement>(null);
  const [isActive, setIsActive] = useState(false);

  // 新增：跟随鼠标的光效状态
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // 使用 useRef 存储目标位置，而不是 useState
  const targetPositionRef = useRef({ x: 0, y: 0 });

  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // 处理鼠标移动，更新目标位置
  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!buttonContainerRef.current) return;

    const rect = buttonContainerRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    targetPositionRef.current = { x, y };
  }, []);

  // 处理鼠标离开
  const handleMouseLeave = useCallback(() => {
    if (!buttonContainerRef.current) return;

    // 鼠标离开时，将目标位置设置为按钮中心
    const width = buttonContainerRef.current.offsetWidth;
    const height = buttonContainerRef.current.offsetHeight;

    targetPositionRef.current = { x: width / 2, y: height / 2 };
  }, []);

  // 平滑动画效果
  useEffect(() => {
    const animatePosition = () => {
      setMousePosition((prev) => {
        // 平滑过渡到目标位置
        const dx = (targetPositionRef.current.x - prev.x) * 0.15;
        const dy = (targetPositionRef.current.y - prev.y) * 0.15;

        return {
          x: prev.x + dx,
          y: prev.y + dy,
        };
      });

      animationFrameRef.current = requestAnimationFrame(animatePosition);
    };

    animationFrameRef.current = requestAnimationFrame(animatePosition);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []); // 空依赖数组，只在组件挂载时运行一次

  // 初始化鼠标位置为按钮中心
  useEffect(() => {
    if (!buttonContainerRef.current) return;

    const width = buttonContainerRef.current.offsetWidth;
    const height = buttonContainerRef.current.offsetHeight;

    setMousePosition({ x: width / 2, y: height / 2 });
    targetPositionRef.current = { x: width / 2, y: height / 2 };
  }, []);

  // 创建波纹效果
  const createRipple = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const button = event.currentTarget;
    const rippleContainer = rippleRef.current;

    if (!rippleContainer) return;

    // 清除现有的波纹
    const existingRipples = rippleContainer.querySelectorAll('.ripple');
    existingRipples.forEach((ripple) => {
      ripple.remove();
    });

    // 创建新的波纹元素
    const circle = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    // 获取点击位置相对于按钮的坐标
    const rect = button.getBoundingClientRect();
    const left = event.clientX - rect.left - radius;
    const top = event.clientY - rect.top - radius;

    // 设置波纹样式
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${left}px`;
    circle.style.top = `${top}px`;
    circle.classList.add('ripple');

    // 添加波纹到容器
    rippleContainer.appendChild(circle);

    // 动画结束后移除波纹
    setTimeout(() => {
      circle.remove();
    }, 600);
  }, []);

  // 处理点击事件
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // 如果是移动设备，显示 ReqDrawer 而不是执行默认操作
      if (isMobile) {
        event.preventDefault();
        event.stopPropagation();
        setShowReqDrawer(true);
        return;
      }

      createRipple(event);
      setIsActive(true);
      setTimeout(() => setIsActive(false), 150);
      if (onClick) {
        onClick();
      }
    },
    [onClick, createRipple, isMobile],
  );

  // 基础类名
  const baseClasses = `
    button 
    relative 
    inline-flex 
    items-center 
    justify-center 
    h-11 
    transition-all 
    duration-300
    ${isActive ? 'btn-active' : ''}
    px-4
    text-n-8
    cursor-pointer
    ${className || ''}
  `;

  // 按钮主体样式 - 只保留白色背景
  const buttonBodyClasses = `
    absolute 
    inset-0 
    bg-n-1
    clip-path-button
  `;

  // 按钮边框发光效果
  const glowEffectClasses = `
    absolute 
    inset-0 
    bg-n-1
    clip-path-button
    blur-[6px]
    opacity-0
    transition-opacity
    duration-300
    -z-10
    [@media(hover:hover)]:group-hover:opacity-70
  `;

  // 新增：跟随鼠标的发光效果
  const mouseGlowEffectClasses = `
    absolute 
    inset-0 
    bg-gradient-to-br 
    from-color-primary/40 
    via-transparent 
    to-color-secondary/40
    blur-[12px] 
    opacity-0 
    transition-opacity
    duration-500
    -z-10
    [@media(hover:hover)]:group-hover:opacity-70
  `;

  // 新增：动态光效层
  const dynamicLightEffectClasses = `
    absolute 
    w-[180px] 
    h-[180px] 
    rounded-full 
    blur-[25px] 
    opacity-0 
    mix-blend-screen 
    z-1
    bg-gradient-to-r 
    from-color-primary/50
    via-color-secondary/30
    to-color-primary/50
    pointer-events-none
    transition-opacity
    duration-500
    [@media(hover:hover)]:group-hover:opacity-100
  `;

  // 新增：额外的光晕效果
  const extraGlowEffectClasses = `
    absolute 
    w-[100px] 
    h-[100px] 
    rounded-full 
    blur-[15px] 
    opacity-0 
    mix-blend-screen 
    z-1
    bg-color-primary/60
    pointer-events-none
    transition-opacity
    duration-300
    animate-pulse
    [@media(hover:hover)]:group-hover:opacity-80
  `;

  // 装饰元素
  const decorationElements = (
    <>
      <span className="absolute left-[10%] right-[10%] top-[6px] h-[1.2px] bg-gradient-to-r from-transparent via-color-secondary/50 to-transparent"></span>
      <span className="absolute bottom-[6px] left-[10%] right-[10%] h-[1.2px] bg-gradient-to-r from-transparent via-color-primary/50 to-transparent"></span>
      <span className="absolute bottom-[10%] left-[6px] top-[10%] w-[1.2px] bg-gradient-to-b from-transparent via-color-secondary/50 to-transparent"></span>
      <span className="absolute bottom-[10%] right-[6px] top-[10%] w-[1.2px] bg-gradient-to-b from-transparent via-color-primary/50 to-transparent"></span>
    </>
  );

  // 角落装饰
  const cornerElements = (
    <>
      <span className="absolute left-0 top-0 h-[12px] w-[12px] border-l-[2px] border-t-[2px] border-color-secondary/70"></span>
      <span className="absolute right-0 top-0 h-[12px] w-[12px] border-r-[2px] border-t-[2px] border-color-primary/70"></span>
      <span className="absolute bottom-0 left-0 h-[12px] w-[12px] border-b-[2px] border-l-[2px] border-color-secondary/70"></span>
      <span className="absolute bottom-0 right-0 h-[12px] w-[12px] border-b-[2px] border-r-[2px] border-color-primary/70"></span>
    </>
  );

  // 扫描线动画
  const scanlineElement = (
    <span className="absolute left-0 top-0 h-full w-full -translate-x-full bg-gradient-to-r from-transparent via-color-secondary/10 to-transparent [@media(hover:hover)]:group-hover:animate-scanline"></span>
  );

  // 按钮内容和req标签 */}
  const buttonContent = (
    <div className="relative z-10 flex items-center gap-3">
      <Box component={CibWindows} className="h-4 w-4 opacity-90 brightness-0" />
      <span className="text-base font-bold">{children}</span>
      <ReqTooltip
        showDrawer={showReqDrawer}
        onDrawerClose={() => setShowReqDrawer(false)}
        downloadUrl={href || ''}
        onClick={() => isMobile && setShowReqDrawer(true)}
      />
    </div>
  );

  // 使用 React 的动态组件特性减少重复代码
  const Tag = href ? 'a' : 'button';

  // 处理共同和特定属性
  const commonProps = {
    className: `group ${baseClasses}`,
    onClick: handleClick,
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
    style: { cursor: 'pointer' }, // 直接在元素上设置光标样式
  };

  // 确定最终的 props (添加 href 如果是链接)
  const finalProps = {
    ...commonProps,
    ...(href
      ? {
          href,
          ref: buttonRef as React.RefObject<HTMLAnchorElement>,
        }
      : {
          ref: buttonRef as React.RefObject<HTMLButtonElement>,
        }),
  };

  return (
    // @ts-ignore - TypeScript 限制，但这是有效的 JSX
    <Tag {...finalProps}>
      {/* 按钮容器 - 用于跟踪鼠标位置 */}
      <div ref={buttonContainerRef} className="absolute inset-0 z-0"></div>

      {/* 主体背景 */}
      <span className={buttonBodyClasses}></span>

      {/* 发光效果 */}
      <span className={glowEffectClasses}></span>

      {/* 跟随鼠标的发光效果 */}
      <span
        className={mouseGlowEffectClasses}
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(250, 148, 28, 0.4), transparent 70%, rgba(111, 67, 243, 0.4))`,
        }}
      ></span>

      {/* 动态光效层 */}
      <span
        className={dynamicLightEffectClasses}
        style={{
          left: `${mousePosition.x - 90}px`,
          top: `${mousePosition.y - 90}px`,
        }}
      ></span>

      {/* 额外的光晕效果 */}
      <span
        className={extraGlowEffectClasses}
        style={{
          left: `${mousePosition.x - 50}px`,
          top: `${mousePosition.y - 50}px`,
          transform: `scale(${isActive ? 1.5 : 1})`,
        }}
      ></span>

      {/* 装饰元素 */}
      <span className="absolute inset-0 z-0 overflow-hidden clip-path-button">{decorationElements}</span>

      {/* 扫描线动画 */}
      <span className="absolute inset-0 z-0 overflow-hidden clip-path-button">{scanlineElement}</span>

      {/* 点击波纹 */}
      <span ref={rippleRef} className="absolute inset-0 z-0 overflow-hidden clip-path-cyber"></span>

      {/* 角落装饰 */}
      {cornerElements}

      {/* 按钮内容 */}
      {buttonContent}
    </Tag>
  );
};

export default CyberButton;
